import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, Menu, X, Cloud, Server, Shield, Workflow, BarChart3, Sparkles,
  Layers, Users, Building2, Cpu, Zap, Brain, Gauge, ShieldCheck, LifeBuoy,
  Award, FileText, HelpCircle, Target, TrendingUp, UserPlus, Star,
  Wrench, Headphones, ArrowRightLeft, Puzzle, Globe, Flag, Landmark, Package,
  MessageSquare,
} from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { trackButtonClick, trackNavigation } from '../utils/analytics';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [closeTimeout, setCloseTimeout] = useState(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuEnter = (menuKey) => {
    if (closeTimeout) { clearTimeout(closeTimeout); setCloseTimeout(null); }
    setActiveMegaMenu(menuKey);
  };

  const handleMenuLeave = () => {
    const timeout = setTimeout(() => setActiveMegaMenu(null), 300);
    setCloseTimeout(timeout);
  };

  const megaMenuData = {
    'What We Do': {
      sections: [
        {
          title: 'SAP Business One Cloud Hosting',
          hubLink: { name: 'View all plans →', link: '/sap-b1-cloud-hosting' },
          items: [
            { name: 'C4P Standard', description: 'Fastest path to SAP B1 in the cloud', icon: Users, link: '/c4p-standard' },
            { name: 'C4P Advanced', description: 'Dedicated infrastructure, built for scale', icon: Server, link: '/c4p-advanced' },
            { name: 'C4P MTPro', description: 'Purpose-built multi-tenant environment', icon: Layers, link: '/mt-pro' },
            { name: "What's Included", description: 'Full feature breakdown across all three plans', icon: Package, link: '/sap-b1-cloud-hosting' },
          ],
        },
        {
          title: 'Platform',
          items: [
            { name: 'Architecture 4.0', description: 'AWS WorkSpaces, Okta, world-first SAP-certified infrastructure', icon: Cpu, link: '/architecture-4' },
            { name: 'Migration Factory', description: 'Land. Expand. Grow. Repeatable cloud migration', icon: ArrowRightLeft, link: '/migration-factory' },
          ],
        },
        {
          title: 'Other Cloud Hosting',
          items: [
            { name: 'BPA-Ready', description: 'Business process automation hosting', icon: Cpu, link: '/bpa-ready' },
            { name: 'NewIntelligence', description: 'IBM Cognos powered analytics', icon: Brain, link: '/new-intelligence' },
          ],
        },
      ],
    },
    'Who We Are': {
      sections: [
        {
          title: 'Company',
          items: [
            { name: 'About Us', description: 'Learn about our mission and values', icon: Award, link: '/about-us' },
            { name: 'Testimonials', description: 'Hear from our partners', icon: MessageSquare, link: '/testimonials' },
          ],
        },
        {
          title: 'Resources',
          items: [
            { name: 'Blog / News', description: 'Latest insights and updates', icon: FileText, link: '/blog-news' },
            { name: 'Webinars', description: 'Free sessions for SAP B1 partners', icon: Users, link: '/webinars' },
          ],
        },
        {
          title: 'Support',
          items: [
            { name: 'FAQs', description: 'Common questions answered', icon: HelpCircle, link: '/faqs' },
          ],
        },
      ],
    },
    'The Cloud4Partners Experience': {
      sections: [
        {
          title: 'Why Choose Us',
          items: [
            { name: 'Value Proposition', description: 'Why choose Cloud4Partners', icon: Target, link: '/value-proposition' },
            { name: 'Partner Benefits', description: 'Comprehensive partner benefits', icon: TrendingUp, link: '/partner-benefits' },
            { name: 'Security & Compliance', description: 'AWS security & compliance for SAP B1', icon: Shield, link: '/security-compliance' },
          ],
        },
        {
          title: 'Partner Journey',
          items: [
            { name: 'Partners', description: 'How the partner model works', icon: Users, link: '/partners' },
            { name: 'Platform Overview', description: 'Managed AWS for SAP B1 partners', icon: Cloud, link: '/platform-overview' },
            { name: 'Partner Cloud Offering', description: 'White-label AWS platform', icon: Users, link: '/partner-cloud-offering' },
            { name: 'Onboarding Process', description: 'Smooth onboarding journey', icon: UserPlus, link: '/onboarding-process' },
          ],
        },
        {
          title: 'Data Sovereignty',
          items: [
            { name: 'Data Sovereignty Overview', description: 'Control, compliance & customer trust', icon: Shield, link: '/data-sovereignty' },
            { name: 'European Compliance', description: 'GDPR & EU data residency', icon: Landmark, link: '/data-sovereignty/europe' },
            { name: 'US Regulatory Solutions', description: 'CCPA, HIPAA & FedRAMP ready', icon: Building2, link: '/data-sovereignty/usa' },
            { name: 'Canadian Data Solutions', description: 'PIPEDA compliance', icon: Flag, link: '/data-sovereignty/canada' },
            { name: 'Middle East Excellence', description: 'UAE PDPL compliance', icon: Star, link: '/data-sovereignty/uae' },
            { name: 'African Growth Markets', description: 'POPIA compliance', icon: TrendingUp, link: '/data-sovereignty/africa' },
          ],
        },
      ],
    },
    'SAP B1 Hub': {
      sections: [
        {
          title: 'Features',
          items: [
            { name: 'Web Client Hosting', description: 'Browser-based SAP B1 access', icon: Globe, link: '/sap-b1/web-client-hosting' },
          ],
        },
        {
          title: 'Resources',
          items: [
            { name: 'Ecosystem Marketplace', description: 'SAP B1 add-ons and integrations', icon: Package, link: '/sap-b1/marketplace' },
            { name: 'Updates & Innovation', description: 'Latest SAP B1 developments', icon: Sparkles, link: '/sap-b1/updates' },
            { name: "What's New in SAP B1", description: 'New features and releases', icon: Star, link: '/sap-b1/whats-new' },
          ],
        },
        {
          title: 'AWS Services',
          items: [
            { name: 'Infrastructure as a Service', description: '10+ years AWS expertise', icon: Gauge, link: '/infrastructure-service' },
            { name: 'DR as a Service', description: 'Disaster recovery solutions', icon: ShieldCheck, link: '/disaster-recovery' },
          ],
        },
      ],
    },
    'How We Help': {
      sections: [
        {
          title: 'Services',
          items: [
            { name: 'Project Management', description: 'Expert project oversight', icon: Wrench, link: '/project-management' },
            { name: 'Customer Support', description: '4.9/5 rated support', icon: Headphones, link: '/customer-support' },
          ],
        },
        {
          title: 'Technical Support',
          items: [
            { name: 'Cloud Migrations', description: 'Seamless cloud migrations', icon: ArrowRightLeft, link: '/cloud-migrations' },
            { name: 'Integrations', description: 'System integration expertise', icon: Puzzle, link: '/integrations' },
          ],
        },
      ],
    },
  };

  return (
    <>
      <nav className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                src="/logos/c4p-transparent.png"
                alt="Cloud4Partners"
                className="h-24 w-auto"
              />
            </Link>

            <div
              className="hidden lg:flex items-center space-x-1"
              onMouseLeave={handleMenuLeave}
            >
              {Object.keys(megaMenuData).map((menuKey) => (
                <div key={menuKey} className="relative" onMouseEnter={() => handleMenuEnter(menuKey)}>
                  <button className="px-4 py-2 text-slate-700 hover:text-sky-600 font-medium flex items-center gap-1 transition-colors">
                    {menuKey}
                    <ChevronDown className={`w-4 h-4 transition-transform ${activeMegaMenu === menuKey ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              {/* Webinars — with live pulse indicator */}
              <Link
                to="/webinars"
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 px-4 py-2 rounded-lg transition-all duration-200"
                data-testid="nav-webinars-btn"
              >
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                Webinars
              </Link>
              <Link to="/contact" onClick={() => trackButtonClick('Get in Touch', 'navbar')}>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Get in Touch
                </Button>
              </Link>
              <Link to="/portal/login" onClick={() => trackButtonClick('Partner Login', 'navbar')}>
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                  Partner Login
                </Button>
              </Link>
            </div>

            <button
              className="lg:hidden p-2 text-slate-700 hover:text-sky-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {activeMegaMenu && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 top-full bg-white border-t shadow-2xl z-50"
              style={{ marginTop: '0px' }}
              onMouseEnter={() => { if (closeTimeout) { clearTimeout(closeTimeout); setCloseTimeout(null); } }}
              onMouseLeave={handleMenuLeave}
            >
              <div className="container mx-auto px-4 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {megaMenuData[activeMegaMenu].sections.map((section, idx) => (
                    <div key={idx} className="relative">
                      {idx < megaMenuData[activeMegaMenu].sections.length - 1 && (
                        <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
                      )}
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {section.title}
                        </h3>
                        {section.hubLink && (
                          <Link to={section.hubLink.link} className="text-xs text-sky-600 hover:underline font-medium" onClick={() => setActiveMegaMenu(null)}>
                            {section.hubLink.name}
                          </Link>
                        )}
                      </div>
                      <div className="space-y-1">
                        {section.items.map((item, itemIdx) => {
                          const ItemIcon = item.icon;
                          return (
                            <Link
                              key={itemIdx}
                              to={item.link || '#'}
                              className="block group"
                              onClick={() => setActiveMegaMenu(null)}
                            >
                              <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gradient-to-r hover:from-sky-50 hover:to-purple-50 transition-all duration-200 hover:shadow-md border border-transparent hover:border-sky-100">
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500/10 to-purple-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-sky-500 group-hover:to-purple-500 transition-all duration-200">
                                  {ItemIcon && <ItemIcon className="w-6 h-6 text-sky-600 group-hover:text-white transition-colors" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors text-sm">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-slate-500 group-hover:text-slate-600 line-clamp-1">{item.description}</div>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden bg-white"
          >
            <div className="h-full overflow-y-auto pt-24 pb-8 px-4">
              <div className="space-y-6">
                {Object.entries(megaMenuData).map(([menuKey, menuData]) => (
                  <div key={menuKey}>
                    <div className="text-lg font-bold text-slate-900 mb-3">{menuKey}</div>
                    {menuData.sections.map((section, idx) => (
                      <div key={idx} className="ml-4 mb-4">
                        <div className="text-sm font-semibold text-sky-600 mb-2">{section.title}</div>
                        <div className="space-y-2">
                          {section.items.map((item, itemIdx) => (
                            <Link
                              key={itemIdx}
                              to={item.link || '#'}
                              className="block text-slate-600 hover:text-sky-600 text-sm py-1"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <Link to="/webinars" onClick={() => { setMobileMenuOpen(false); }}>
                  <Button className="w-full mb-2 bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-bold">
                    <span className="flex h-2 w-2 relative mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    Webinars
                  </Button>
                </Link>
                <Link to="/contact" onClick={() => { setMobileMenuOpen(false); trackButtonClick('Get in Touch', 'mobile_menu'); }}>
                  <Button className="w-full mb-2 bg-gradient-to-r from-sky-500 to-purple-500 hover:from-sky-600 hover:to-purple-600 text-white">
                    Get in Touch
                  </Button>
                </Link>
                <Link to="/portal/login" onClick={() => { setMobileMenuOpen(false); trackButtonClick('Partner Login', 'mobile_menu'); }}>
                  <Button variant="outline" className="w-full border-sky-500 text-sky-600">
                    Partner Login
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
